import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { Search, ArrowRight } from 'lucide-react';
import { useAppStore } from '@/store/appStore';

const commands = [
  { label: 'Overview', path: '/', icon: '📊' },
  { label: 'Devices', path: '/devices', icon: '🖥️' },
  { label: 'Device Details', path: '/devices/dev-1', icon: '🖥️' },
  { label: 'Sites', path: '/sites', icon: '📍' },
  { label: 'Clients', path: '/clients', icon: '🏢' },
  { label: 'Map', path: '/map', icon: '🗺️' },
  { label: 'Alerts', path: '/alerts', icon: '🔔' },
  { label: 'Automation', path: '/automation', icon: '⚡' },
  { label: 'Scripts', path: '/scripts', icon: '📜' },
  { label: 'Patches', path: '/patches', icon: '🛡️' },
  { label: 'Software', path: '/software', icon: '📦' },
  { label: 'Remote Sessions', path: '/remote-sessions', icon: '🖥️' },
  { label: 'Jobs', path: '/jobs', icon: '💼' },
  { label: 'Reports', path: '/reports', icon: '📄' },
  { label: 'Audit Log', path: '/audit-log', icon: '📋' },
  { label: 'Users & Roles', path: '/users', icon: '👥' },
  { label: 'Settings', path: '/settings', icon: '⚙️' },
  { label: 'Remote Desktop', path: '/devices/dev-1/remote-desktop', icon: '🖥️' },
  { label: 'File Explorer', path: '/devices/dev-1/files', icon: '📁' },
  { label: 'PowerShell Terminal', path: '/devices/dev-1/powershell', icon: '💻' },
  { label: 'CMD Terminal', path: '/devices/dev-1/cmd', icon: '💻' },
];

export function CommandPalette() {
  const open = useAppStore((s) => s.commandPaletteOpen);
  const setOpen = useAppStore((s) => s.setCommandPaletteOpen);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);

  const filtered = useMemo(() => {
    if (!query) return commands;
    return commands.filter((c) => c.label.toLowerCase().includes(query.toLowerCase()));
  }, [query]);

  useEffect(() => {
    if (!open) { setQuery(''); setSelected(0); return; }
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelected((s) => Math.min(s + 1, filtered.length - 1)); }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSelected((s) => Math.max(s - 1, 0)); }
      if (e.key === 'Enter' && filtered[selected]) {
        navigate(filtered[selected].path);
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, filtered, selected, navigate, setOpen]);

  // Global hotkey
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [setOpen]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[150] flex items-start justify-center pt-24 animate-fade-in">
      <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
      <div className="relative w-full max-w-lg mx-4 surface-elevated animate-slide-up overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <Search className="h-4 w-4 text-text-muted" />
          <input
            autoFocus
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelected(0); }}
            placeholder="Type a command or search…"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-text-muted"
          />
          <kbd className="text-2xs bg-base-bg px-1.5 py-0.5 rounded border border-border text-text-muted">ESC</kbd>
        </div>
        <div className="max-h-80 overflow-y-auto py-1">
          {filtered.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-text-muted">No results found</div>
          )}
          {filtered.map((cmd, i) => (
            <button
              key={cmd.path + cmd.label}
              onClick={() => { navigate(cmd.path); setOpen(false); }}
              onMouseEnter={() => setSelected(i)}
              className={cn(
                'w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors',
                i === selected ? 'bg-base-hover text-text-primary' : 'text-text-secondary',
              )}
            >
              <span className="text-base">{cmd.icon}</span>
              <span className="flex-1 text-left">{cmd.label}</span>
              {i === selected && <ArrowRight className="h-3.5 w-3.5" />}
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
